/**
 * Which cave a session position means, per mode (docs/spec/02 §9, 03 §4). Pure: the game plays
 * these turns, and the score check (verify.ts) replays the same ones.
 */

import { caveSet, findCave, PLAY_ORDER, playOrder } from '../caves';
import { dailyCave, dailyNumber, expeditionCave } from '../caves/generator';
import type { CaveDef, CaveSetId } from '../caves/types';
import type { Player } from './session';

export interface CaveTurn {
  readonly def: CaveDef;
  readonly level: number;
  /** `A/1`, `DEPTH 4`, `#266`. */
  readonly label: string;
  /** The set a Classic or Deep cave belongs to, for music and records. */
  readonly set: CaveSetId | null;
}

/** Start position in the play order for a start cave letter (A, E, I, M). */
export function startPosition(letter: string): number {
  return Math.max(0, PLAY_ORDER.indexOf(letter));
}

function classicA(): CaveDef {
  const def = findCave(caveSet('classic'), 'A');
  if (!def) throw new Error('no caves');
  return def;
}

/** A Classic or Deep game: the play order, a level per round. */
export function setTurn(id: CaveSetId, p: Player): CaveTurn {
  const set = caveSet(id);
  const letter = PLAY_ORDER[p.position % PLAY_ORDER.length] ?? 'A';
  const def = findCave(set, letter) ?? playOrder(set)[0] ?? classicA();
  return { def, level: p.level, label: `${def.id}/${p.level}`, set: id };
}

export function expeditionTurn(seed: number, p: Player): CaveTurn {
  const depth = p.position + 1;
  return { def: expeditionCave(seed, depth), level: 1, label: `DEPTH ${depth}`, set: null };
}

export function dailyTurn(year: number, month: number, day: number): CaveTurn {
  const n = dailyNumber(year, month, day);
  return { def: dailyCave(year, month, day), level: 1, label: `#${n}`, set: null };
}

export function demoTurn(): CaveTurn {
  return { def: classicA(), level: 1, label: 'A/1', set: 'classic' };
}
